import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'

import type { CustomerFormData } from '../model/customer-schema'
import { useCustomerForm } from '../model/use-customer-form'
import { CustomerCard } from './customer-card'
import type { CustomerSavedField } from './customer-card'

export interface EditCustomerPayload {
  customer: CustomerFormData
}

export interface EditCustomerDefaults {
  customer?: Partial<CustomerFormData>
}

export interface EditCustomerViewProps {
  defaultValues?: EditCustomerDefaults
  title?: string
  isSaving?: boolean
  onCancel?: () => void
  onSubmit?: (data: EditCustomerPayload) => void | Promise<void>
  className?: string
}

export function EditCustomerView({
  defaultValues,
  title,
  isSaving = false,
  onCancel,
  onSubmit,
  className,
}: EditCustomerViewProps) {
  const { t } = useTranslation()
  const customer = useCustomerForm({
    defaultValues: defaultValues?.customer,
    onSubmit: (data) => onSubmit?.({ customer: data }),
  })
  const [customerSavedField, setCustomerSavedField] =
    useState<CustomerSavedField | null>(null)

  const handleSubmit = customer.handleSubmit
  const submitting = isSaving || customer.isSubmitting

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-2.5 rounded-md border border-gray-200 bg-white pt-2.5 pb-5',
        className,
      )}
    >
      <div className="flex flex-col gap-1.25">
        <div className="flex h-8 items-center justify-between px-5 pb-1.25">
          <h3 className="text-base font-semibold text-gray-900">
            {title ?? t('orderEdit.customer.title')}
          </h3>
          <div className="flex items-center gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="xs"
              pressEffect
              className="px-3.5"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('orderEdit.view.cancel')}
            </Button>
            <Button
              type="button"
              size="xs"
              pressEffect
              className="px-3.5"
              onClick={() => void handleSubmit?.()}
              disabled={submitting}
            >
              {submitting ? t('table.saving') : t('orderEdit.view.save')}
            </Button>
          </div>
        </div>
        <div className="h-px w-full bg-gray-200" />
      </div>

      <div className="edit-form-fields px-2.5">
        <CustomerCard
          bare
          searchPlaceholders
          control={customer.control}
          register={customer.register}
          setValue={customer.setValue}
          errors={customer.errors}
          onCustomerSelect={() => setCustomerSavedField(null)}
          customerSavedField={customerSavedField}
          onSaveAsNewCustomer={setCustomerSavedField}
          onClearNewCustomer={() => setCustomerSavedField(null)}
        />
      </div>
    </div>
  )
}
